import React, { createContext, useContext, useState, useEffect } from 'react';
import { Conversation } from '../types';
import { getSessionUser } from '../services/userService';

export interface CartItem {
  id: number;
  name: string;
  price: number;
  imageUrl: string;
  imageWebpUrl?: string | null;
  slug: string;
  quantity: number;
}

interface AppContextType {
  // Cart
  items: CartItem[];
  addToCart: (product: any, quantity?: number) => void;
  removeFromCart: (id: number) => void;
  updateQuantity: (id: number, quantity: number) => void;
  clearCart: () => void;
  cartCount: number;
  cartTotal: number;

  // Chat
  unreadCount: number;
  conversations: Conversation[];
  setConversations: React.Dispatch<React.SetStateAction<Conversation[]>>;
}

const CartContext = createContext<AppContextType | undefined>(undefined);

export const CartProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  // Cart State
  const [items, setItems] = useState<CartItem[]>([]);
  
  // Chat State
  const [unreadCount, setUnreadCount] = useState(0);
  const [conversations, setConversations] = useState<Conversation[]>([]);

  // Cart Effects (Load from LocalStorage)
  useEffect(() => {
    const savedCart = localStorage.getItem('esoteric_cart');
    if (savedCart) {
      try {
        setItems(JSON.parse(savedCart));
      } catch (e) {
        console.error("Failed to parse cart", e);
      }
    }
  }, []);

  // Cart Effects (Save to LocalStorage)
  useEffect(() => {
    localStorage.setItem('esoteric_cart', JSON.stringify(items));
  }, [items]);

  // Chat Polling Effect
  useEffect(() => {
    const user = getSessionUser();
    if (!user?.id) {
        setUnreadCount(0);
        return;
    }

    const pollData = async () => {
      try {
        const headers = { 'x-user-id': String(user.id) };
        const [unreadRes, convosRes] = await Promise.all([
            fetch('/api/chat?unread_count=true', { headers }),
            fetch('/api/chat?list_conversations=true', { headers })
        ]);

        if (unreadRes.ok) {
          const data = await unreadRes.json();
          setUnreadCount(data.unreadCount || 0);
        }

        if (convosRes.ok) {
            const { conversations: fetchedConvos } = await convosRes.json();
            setConversations(fetchedConvos);
        }
      } catch (e) {
        console.error("Chat poll failed:", e);
      }
    };

    pollData(); // Initial fetch
    const interval = setInterval(pollData, 5000); // Poll every 5 seconds

    return () => clearInterval(interval);
  }, []);


  const addToCart = (product: any, quantity = 1) => {
    setItems(prev => {
      const existing = prev.find(item => item.id === product.id);
      if (existing) {
        return prev.map(item => 
          item.id === product.id 
            ? { ...item, quantity: item.quantity + quantity }
            : item
        );
      }
      
      let imageUrl = product.mediaUrl || product.thumbnail || '';
      let imageWebpUrl = product.mediaWebpUrl || product.thumbnail_webp || null;

      return [...prev, {
        id: product.id,
        name: product.name,
        price: product.price,
        imageUrl: imageUrl,
        imageWebpUrl: imageWebpUrl,
        slug: product.slug,
        quantity: quantity
      }];
    });
  };

  const removeFromCart = (id: number) => {
    setItems(prev => prev.filter(item => item.id !== id));
  };

  const updateQuantity = (id: number, quantity: number) => {
    if (quantity < 1) return;
    setItems(prev => prev.map(item => item.id === id ? { ...item, quantity } : item));
  };

  const clearCart = () => {
    setItems([]);
  };

  const cartCount = items.reduce((sum, item) => sum + item.quantity, 0);
  const cartTotal = items.reduce((sum, item) => sum + (item.price * item.quantity), 0);
  
  const value = {
    items, addToCart, removeFromCart, updateQuantity, clearCart, cartCount, cartTotal,
    unreadCount, conversations, setConversations
  };

  return (
    <CartContext.Provider value={value}>
      {children}
    </CartContext.Provider>
  );
};

export const useCart = () => {
  const context = useContext(CartContext);
  if (context === undefined) {
    throw new Error('useCart must be used within a CartProvider');
  }
  return context;
};